import type { WorkareaTab, ConnectionConfig } from '@/types';

/**
 * Helpers for the sidebar's "Collapse All" / "Expand to Active Tab" buttons.
 * Key shapes must match the ones ConnectionTree writes via `toggle(...)`.
 */

export interface ExpandTarget {
  /** Every tree key that must flip to `true` to reveal the target row. */
  keysToExpand: string[];
  /** The leaf node's `data-tree-key` so the caller can scroll it into view. */
  scrollKey: string | null;
}

/**
 * Given an active tab and the connections list, compute which tree nodes
 * must be expanded to show the tab's source object. Returns null when the
 * tab has no natural position in the connections tree (tunnel, terminal).
 *
 * The database name falls back to the connection's own `databaseName`
 * when the tab doesn't carry one (legacy tabs or single-DB drivers).
 */
export function resolveExpandTarget(
  tab: WorkareaTab | null,
  connections: ConnectionConfig[],
): ExpandTarget | null {
  if (!tab) return null;

  const findConnection = (id: string) => connections.find((c) => c.id === id);

  // Tabs with no connections-tree home — nothing to expand.
  if (tab.type === 'tunnel' || tab.type === 'terminal' || tab.type === 'markdown' || tab.type === 'admin-shell') return null;

  // Connection info tab lives at the connection level.
  if (tab.type === 'info' && tab.objectType === 'connection') {
    return {
      keysToExpand: [`conn:${tab.connectionId}`],
      scrollKey: `conn:${tab.connectionId}`,
    };
  }

  const conn = findConnection(tab.connectionId);
  if (!conn) return null;

  const dbName =
    ('database' in tab && tab.database) ? tab.database : conn.databaseName;
  if (!dbName) return null;

  const connKey = `conn:${tab.connectionId}`;
  const dbKey = `db:${tab.connectionId}:${dbName}`;

  // Database info tab → through the connection + database.
  if (tab.type === 'info' && tab.objectType === 'database') {
    return { keysToExpand: [connKey, dbKey], scrollKey: dbKey };
  }

  // Table / structure / designer / code-editor — all schema-scoped.
  if ('schema' in tab && tab.schema) {
    const schemaKey = `schema:${tab.connectionId}:${dbName}:${tab.schema}`;
    const baseKeys = [connKey, dbKey];

    // When the schema name matches the database name, the tree flattens
    // and there's no schema-level row — don't set its key. Others stay.
    const schemaMatchesDb = tab.schema.toLowerCase() === dbName.toLowerCase();
    if (!schemaMatchesDb) baseKeys.push(schemaKey);

    // Table-ish tabs land under the Tables folder. Designer in `create`
    // mode has no tableName, so just expand to the folder.
    if (tab.type === 'table' || tab.type === 'structure') {
      const tablesKey = `tables:${tab.connectionId}:${dbName}:${tab.schema}`;
      const tableKey = `table:${tab.connectionId}:${tab.schema}.${tab.table}`;
      return {
        keysToExpand: [...baseKeys, tablesKey],
        scrollKey: tableKey,
      };
    }

    if (tab.type === 'designer') {
      const tablesKey = `tables:${tab.connectionId}:${dbName}:${tab.schema}`;
      const tableKey = tab.tableName
        ? `table:${tab.connectionId}:${tab.schema}.${tab.tableName}`
        : tablesKey;
      return {
        keysToExpand: [...baseKeys, tablesKey],
        scrollKey: tableKey,
      };
    }

    if (tab.type === 'code-editor') {
      const folderKey = tab.objectType === 'view'
        ? `views:${tab.connectionId}:${dbName}:${tab.schema}`
        : `funcs:${tab.connectionId}:${dbName}:${tab.schema}`;
      return {
        keysToExpand: [...baseKeys, folderKey],
        scrollKey: folderKey,
      };
    }
  }

  // Query / ERD / diff — no schema, reveal down to the database.
  if (tab.type === 'query' || tab.type === 'erd' || tab.type === 'diff') {
    return { keysToExpand: [connKey, dbKey], scrollKey: dbKey };
  }

  return { keysToExpand: [connKey], scrollKey: connKey };
}

/**
 * Find the row DOM element for a tree key and scroll it into view. The
 * sidebar uses `data-tree-key` on each TreeNode row; this matches against
 * whichever exists (leaf rows first, fall back to the parent's). Runs
 * after a paint so freshly-expanded rows are in the DOM before we scroll.
 */
export function scrollTreeKeyIntoView(key: string): void {
  requestAnimationFrame(() => {
    // Double RAF — the first pass renders the newly-expanded parents,
    // the second lets the deeper rows mount before we query for them.
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-tree-key="${CSS.escape(key)}"]`);
      if (el instanceof HTMLElement) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    });
  });
}
